import React, { useState } from 'react';
import { getSourceDisplayName, getSourceUrl, getFaultCodeReferenceLinks } from '../utils/sourceRegistry';
import DocViewerModal from './DocViewerModal';

/** Wrap fault codes (e.g. P0115) and quoted phrases in highlight spans for reasoning text */
function renderReasoningWithHighlights(reasoningText) {
  if (!reasoningText || typeof reasoningText !== 'string') return reasoningText;
  const parts = [];
  const faultCodeRe = /\b(P[0-9]{4})\b/gi;
  const quoteRe = /'([^']+)'/g;
  let lastIndex = 0;
  const allMatches = [];
  let m;
  while ((m = faultCodeRe.exec(reasoningText)) !== null) {
    allMatches.push({ start: m.index, end: m.index + m[0].length, matchText: m[0], key: `f-${m.index}` });
  }
  while ((m = quoteRe.exec(reasoningText)) !== null) {
    allMatches.push({ start: m.index, end: m.index + m[0].length, matchText: m[0], key: `q-${m.index}` });
  }
  allMatches.sort((a, b) => a.start - b.start);
  const merged = [];
  for (const match of allMatches) {
    if (merged.length && match.start < merged[merged.length - 1].end) continue;
    merged.push(match);
  }
  merged.forEach(({ start, end, matchText, key }) => {
    if (start > lastIndex) {
      parts.push({ type: 'text', content: reasoningText.slice(lastIndex, start), key: `t-${lastIndex}` });
    }
    parts.push({ type: 'highlight', content: matchText, key });
    lastIndex = end;
  });
  if (lastIndex < reasoningText.length) {
    parts.push({ type: 'text', content: reasoningText.slice(lastIndex), key: `t-${lastIndex}` });
  }
  if (parts.length === 0) return reasoningText;
  return parts.map((p) =>
    p.type === 'highlight' ? (
      <span key={p.key} className="result-page__highlight">{p.content}</span>
    ) : (
      <span key={p.key}>{p.content}</span>
    )
  );
}

function TriageResult({ result, onProceed, loading, faultCode }) {
  const [docViewer, setDocViewer] = useState({ open: false, url: '', title: '' });
  const riskClass = result.risk_level.toLowerCase();
  const confidencePercent = (result.confidence * 100).toFixed(0);

  const renderMarkdown = (text) => {
    const rawText = text || "";
    try {
      if (window.marked) {
        return { __html: window.marked.parse(rawText) };
      }
    } catch (e) {
      console.error("Markdown Error", e);
    }
    return { __html: rawText };
  };

  return (
    <div className="result-page">
      <h2 className="result-page__title">Triage Result</h2>
      <p className="result-page__subtitle">
        AI analysis of reported fault
      </p>

      {result.offline && (
        <div className="result-page__offline">
          ⚠ Offline triage — rules-based classification. Full AI analysis on reconnect.
        </div>
      )}

      <div className="card result-page__card">
        <div className="result-page__row">
          <div>
            <div className="result-page__label">Category</div>
            <div className="result-page__value">
              {result.category.replace(/_/g, ' ')}
            </div>
          </div>
          <span className={`badge badge--${riskClass}`}>
            {result.risk_level} Risk
          </span>
        </div>

        <div className="result-page__box result-page__confidence-box">
          <div className="result-page__label">AI Confidence</div>
          <div className="confidence-meter">
            <div className="confidence-meter__bar">
              <div
                className="confidence-meter__fill"
                style={{ width: `${confidencePercent}%` }}
              />
            </div>
            <span className="confidence-meter__label">{confidencePercent}%</span>
          </div>
        </div>

        <div className="result-page__box result-page__reasoning-box">
          <div className="result-page__label">Reasoning</div>
          <div className="result-page__reasoning-body">
            {renderReasoningWithHighlights(result.reasoning)}
          </div>
        </div>

        <div className="result-page__info-box">
          <strong>Next step:</strong>{' '}
          <span dangerouslySetInnerHTML={renderMarkdown(result.next_diagnostic_area)} />
        </div>

        {result.sources && result.sources.length > 0 && (
          <div className="result-page__box">
            <div className="result-page__label">Knowledge base used (RAG)</div>
            <p className="result-page__sources-hint">Specific documents used for this triage. Click to open:</p>
            <ul className="result-page__sources rag-sources-list" aria-label="RAG source documents">
              {result.sources.map((src, idx) => {
                const filename = typeof src === 'string' ? src.trim() : String(src);
                const displayName = getSourceDisplayName(filename);
                const href = getSourceUrl(filename);
                return (
                  <li key={idx} className="result-page__source-tag rag-source-item">
                    <span className="rag-source-item__name">{displayName}</span>
                    <button
                      type="button"
                      className="result-page__source-link rag-source-item__link"
                      onClick={() => setDocViewer({ open: true, url: href, title: displayName })}
                      title={`View ${displayName}`}
                    >
                      View document →
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {(faultCode && getFaultCodeReferenceLinks(faultCode).length > 0) && (
          <div className="result-page__box">
            <div className="result-page__label">Fault code reference</div>
            <p className="result-page__sources-hint">Look up <strong>{faultCode}</strong> on external sites:</p>
            <ul className="result-page__sources rag-sources-list" aria-label="Fault code links">
              {getFaultCodeReferenceLinks(faultCode).map((link, idx) => (
                <li key={idx} className="result-page__source-tag rag-source-item">
                  <span className="rag-source-item__name">{link.name}</span>
                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="result-page__source-link rag-source-item__link" title={`Open ${link.name}`}>
                    Open →
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result.requires_escalation && (
          <div className="result-page__notice">
            Supervisor approval will be required before any repair action.
          </div>
        )}

        <DocViewerModal
          isOpen={docViewer.open}
          url={docViewer.url}
          title={docViewer.title}
          onClose={() => setDocViewer({ open: false, url: '', title: '' })}
        />

        <button
          className={`btn btn--primary btn--block ${loading ? 'btn--loading' : ''}`}
          onClick={onProceed}
          disabled={loading}
        >
          {loading ? (
            <span className="btn__spinner-wrap">
              <span className="btn__spinner" aria-hidden />
              <span>Loading evidence steps...</span>
            </span>
          ) : (
            'Proceed to Evidence Collection'
          )}
        </button>
      </div>
    </div>
  );
}

export default TriageResult;
